import { useState } from 'react';
import axios from 'axios';
import { X, Loader2, CheckCircle, Send } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTIES = ['Brevard County', 'Flagler County', 'Lake County', 'Marion County', 'Orange County', 'Osceola County', 'Polk County', 'Putnam County', 'Seminole County', 'Sumter County', 'Volusia County'];
const CONTACT_TIMES = [
  'Morning (8AM - 12PM)',
  'Afternoon (12PM - 5PM)',
  'Evening (5PM - 8PM)',
  'Anytime'
];

export default function EstimateFormModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    county: '',
    email: '',
    best_time_to_contact: '',
    terms_accepted: false,
    product_id: null,
    product_name: 'Free Estimate Request'
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.county) newErrors.county = 'County is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.best_time_to_contact) newErrors.best_time_to_contact = 'Please select a time';
    if (!formData.terms_accepted) newErrors.terms_accepted = 'You must agree to be contacted';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, formData);
      setSubmitted(true);
      toast.success('Thank you! Our team will contact you shortly.');
    } catch (err) {
      console.error('Error submitting estimate:', err);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setFormData({
      name: '', phone: '', address: '', county: '', email: '',
      best_time_to_contact: '', terms_accepted: false,
      product_id: null, product_name: 'Free Estimate Request'
    });
    setErrors({});
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="estimate-form-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <button onClick={handleClose} className="absolute top-3 right-3 p-1.5 hover:bg-gray-100 rounded-full transition-colors z-10" data-testid="estimate-modal-close-btn">
          <X className="w-5 h-5 text-gray-500" />
        </button>

        <div className="p-6">
          {submitted ? (
            <div className="text-center py-8" data-testid="estimate-form-success">
              <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h3 className="font-bold text-xl text-gray-900 mb-2">Thank You!</h3>
              <p className="text-gray-600">Our team will contact you shortly with your free estimate.</p>
              <button onClick={handleClose} className="mt-6 bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors">
                Close
              </button>
            </div>
          ) : (
            <>
              <h3 className="font-bold text-lg text-gray-900 mb-0.5">Get A Free Estimate</h3>
              <p className="text-xs text-gray-500 mb-4">Fill out the form below and our team will contact you shortly.</p>

              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                  <div>
                    <Label className="text-base font-semibold text-gray-900">Full Name *</Label>
                    <Input value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} className={`mt-1 rounded-lg h-11 text-base ${errors.name ? 'border-red-500' : 'border-gray-300'}`} placeholder="John Smith" data-testid="estimate-form-name" />
                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <Label className="text-base font-semibold text-gray-900">Phone Number *</Label>
                    <Input type="tel" value={formData.phone} onChange={(e) => handleInputChange('phone', e.target.value)} className={`mt-1 rounded-lg h-11 text-base ${errors.phone ? 'border-red-500' : 'border-gray-300'}`} placeholder="(555) 123-4567" data-testid="estimate-form-phone" />
                    {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                  </div>
                  <div>
                    <Label className="text-base font-semibold text-gray-900">Email Address *</Label>
                    <Input type="email" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} className={`mt-1 rounded-lg h-11 text-base ${errors.email ? 'border-red-500' : 'border-gray-300'}`} placeholder="john@example.com" data-testid="estimate-form-email" />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <Label className="text-base font-semibold text-gray-900">County *</Label>
                    <Select value={formData.county} onValueChange={(value) => handleInputChange('county', value)}>
                      <SelectTrigger className={`mt-1 rounded-lg h-11 text-base ${errors.county ? 'border-red-500' : 'border-gray-300'}`} data-testid="estimate-form-county">
                        <SelectValue placeholder="Select your county" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {errors.county && <p className="text-red-500 text-xs mt-1">{errors.county}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-base font-semibold text-gray-900">Address *</Label>
                    <Input value={formData.address} onChange={(e) => handleInputChange('address', e.target.value)} className={`mt-1 rounded-lg h-11 text-base ${errors.address ? 'border-red-500' : 'border-gray-300'}`} placeholder="123 Main St, City, FL 32901" data-testid="estimate-form-address" />
                    {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-base font-semibold text-gray-900">Best Time to Contact *</Label>
                    <Select value={formData.best_time_to_contact} onValueChange={(value) => handleInputChange('best_time_to_contact', value)}>
                      <SelectTrigger className={`mt-1 rounded-lg h-11 text-base ${errors.best_time_to_contact ? 'border-red-500' : 'border-gray-300'}`} data-testid="estimate-form-contact-time">
                        <SelectValue placeholder="Select preferred time" />
                      </SelectTrigger>
                      <SelectContent>
                        {CONTACT_TIMES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {errors.best_time_to_contact && <p className="text-red-500 text-xs mt-1">{errors.best_time_to_contact}</p>}
                  </div>
                  <div className="sm:col-span-2 flex items-start gap-3 pt-2">
                    <Checkbox id="estimate-terms" checked={formData.terms_accepted} onCheckedChange={(checked) => handleInputChange('terms_accepted', checked)} className="mt-0.5" data-testid="estimate-form-terms" />
                    <Label htmlFor="estimate-terms" className="text-sm text-gray-700 leading-tight">
                      I Agree to have either Appliance Direct or an Installation Partner contact me.
                    </Label>
                  </div>
                  {errors.terms_accepted && <p className="sm:col-span-2 text-red-500 text-xs">{errors.terms_accepted}</p>}
                  <div className="sm:col-span-2 pt-2">
                    <button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white px-6 py-3.5 font-bold rounded-lg transition-colors flex items-center justify-center gap-2 text-lg" data-testid="estimate-form-submit">
                      {loading ? (
                        <><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</>
                      ) : (
                        <><Send className="w-5 h-5" /> Submit</>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
